/** @jsxImportSource @emotion/react */
import { useMemo } from 'react'
import getAPIDomain from '@signal/utils/env/apiDomain'
import isStorybook from '@signal/utils/env/isStorybook'
import isTest from '@signal/utils/env/isTest'
import isCurrentPathForAnonymousAccess from '@signal/utils/isAnonymousPath'
import { fetchData, fetchDataNull, FetchHandlerType } from '../swr'
import useSWRWithCache from '../swr/hooks'
import { replaceCacheURL } from '../swr/utils'
import { API_SEGMENT, API_VERSION } from './constants'
import { TenantThemeType, UseTenantThemePropsType } from './types'
import mockData from './mockData.json'

const MOCK_THEME = 'mock'
export const NO_THEME = 'none'

const DEFAULT_TENANT_ID = 1

// use this hook from the main app to load tenant theme colors
export function useTenantTheme({
  mockTheme
}: UseTenantThemePropsType): TenantThemeType | undefined {
  const isTestOrStorybook = isStorybook() || isTest()
  if (!mockTheme && isTestOrStorybook) {
    mockTheme = NO_THEME
  }
  const url = useMemo(
    () => `${getAPIDomain(API_SEGMENT)}/v${API_VERSION}/theme`,
    []
  )
  if (mockTheme && (mockTheme !== NO_THEME || isTestOrStorybook)) {
    // NOTE: replace {} with mockData to test the actual mock data
    replaceCacheURL(url, '', mockTheme === MOCK_THEME ? mockData : {})
  }

  const response = useSWRWithCache<TenantThemeType>({
    url,
    fetchHandler: (isCurrentPathForAnonymousAccess()
      ? fetchDataNull
      : fetchData) as FetchHandlerType<TenantThemeType>,
    context: 'useTenantTheme'
  })

  return (
    response && {
      ...response,
      isDefaultTheme: response.tenantId === DEFAULT_TENANT_ID
    }
  )
}

export default useTenantTheme
